import { Menu, Bell, LogOut, Shield, Moon, Sun } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { useTheme } from "../utils/ThemeProvider";
import { toTitleCase } from "../lib/utils";

export function AdminHeader({ onMenuClick }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const getUserInitials = () => {
    const name = user?.name || user?.full_name;
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return "A";
  };

  const getUserName = () => {
    return toTitleCase(user?.name || user?.full_name || "Admin");
  };

  const handleLogout = async () => {
    await logout();
    navigate('/pilot/login');
  };

  return (
    <header className="fixed top-0 left-0 right-0 h-16 bg-card border-b border-border z-50 shadow-sm overflow-x-hidden">
      <div className="flex items-center justify-between h-full px-3 sm:px-4 lg:px-6 gap-2 min-w-0">
        {/* Left Section */}
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={onMenuClick}
          >
            <Menu className="h-6 w-6" />
          </Button>
          
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-[#1E74FD] to-blue-600 text-white">
              <Shield className="h-5 w-5" />
            </div>
            <div className="hidden sm:block">
              <h1 className="text-lg font-bold text-foreground">Admin Portal</h1>
              <p className="text-xs text-muted-foreground">Management Dashboard</p>
            </div>
          </div>
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-1 sm:gap-3 shrink-0">
          <Button variant="ghost" size="icon" onClick={toggleTheme}>
            {theme === "light" ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
          </Button>
          
          <Button variant="ghost" size="icon" className="relative">
            <Bell className="h-5 w-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-[#1E74FD] rounded-full"></span>
          </Button>

          <div className="flex items-center gap-3 pl-3 border-l border-border">
            <Avatar className="h-9 w-9">
              {(user?.avatar || user?.picture_path) && (
                <AvatarImage src={user.avatar || user.picture_path} alt={getUserName()} />
              )}
              <AvatarFallback className="bg-gradient-to-br from-[#1E74FD] to-blue-600 text-white text-sm font-semibold">
                {getUserInitials()}
              </AvatarFallback>
            </Avatar>
            <div className="hidden md:block text-right">
              <p className="text-sm font-medium text-foreground">{getUserName()}</p>
              <p className="text-xs text-muted-foreground">Administrator</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-[#1E74FD] hover:text-[#1E74FD]/80 hover:bg-[#1E74FD]/10"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}
